// Denied & underpaid — history mock (00-ds-09 §9, D148). Deterministic, seeded
// so every reload shows the same ~1,400 rows. UI-only: the real search, filter,
// sort and pagination are server-side (§8); this exists so the demo can page and
// filter a history "at scale". Two histories live here, merged into one at the
// bottom (`CLOSED_HISTORY`) for the "Closed" pill (rcm-18):
//   • Denied › Resolved  — claims that left the open queue (paid after appeal,
//     corrected and paid, written off, billed to the patient).
//   • Appeals sent › Closed — appeals that have reached a final answer.

import { CDT, PAYERS, fullName, generate, int, money, pastDate, pick } from '@/components/lists';
import type { AppealSent } from './types';

// ---- Denied › Resolved ----

export type ResolvedOutcome = 'paid_after_appeal' | 'corrected' | 'written_off' | 'billed_patient';

export const RESOLVED_OUTCOMES: { value: ResolvedOutcome; label: string; cls: string }[] = [
  { value: 'paid_after_appeal', label: 'Paid after appeal', cls: 'b-ok' },
  { value: 'corrected', label: 'Corrected and paid', cls: 'b-ok' },
  { value: 'written_off', label: 'Written off', cls: 'b-mute' },
  { value: 'billed_patient', label: 'Billed to patient', cls: 'b-mute' },
];

const OUTCOME_LABEL: Record<ResolvedOutcome, string> = {
  paid_after_appeal: 'Paid after appeal',
  corrected: 'Corrected and paid',
  written_off: 'Written off',
  billed_patient: 'Billed to patient',
};

export interface DeniedResolvedRow {
  id: string;
  patient: string;
  payer: string;
  codes: string;
  billed: number;
  kind: 'Denied' | 'Underpaid';
  outcome: ResolvedOutcome;
  outcomeLabel: string;
  /** What actually came in — 0 for a write-off or a patient bill. */
  recovered: number;
  resolvedIso: string;
  resolvedLabel: string;
}

function pickCodes(r: () => number): string {
  const first = pick(r, CDT).code;
  if (r() < 0.22) return `${first} ×2`;
  if (r() < 0.14) {
    let second = pick(r, CDT).code;
    if (second === first) second = pick(r, CDT).code;
    return `${first}, ${second}`;
  }
  return first;
}

function pickResolvedOutcome(r: () => number): ResolvedOutcome {
  const n = r();
  if (n < 0.42) return 'paid_after_appeal';
  if (n < 0.68) return 'corrected';
  if (n < 0.85) return 'written_off';
  return 'billed_patient';
}

export const DENIED_RESOLVED: DeniedResolvedRow[] = generate(1400, 0xD4e5, (r, i) => {
  const payer = pick(r, PAYERS);
  const codes = pickCodes(r);
  const billed = money(r, 140, 1850);
  const kind: 'Denied' | 'Underpaid' = r() < 0.55 ? 'Denied' : 'Underpaid';
  const outcome = pickResolvedOutcome(r);
  const { iso, label } = pastDate(r, 24);
  let recovered = 0;
  if (outcome === 'paid_after_appeal') recovered = Math.round(billed * (0.6 + r() * 0.4));
  else if (outcome === 'corrected') recovered = Math.round(billed * (0.7 + r() * 0.3));
  return {
    id: `CLM-${String(30000 + i)}`,
    patient: fullName(r),
    payer,
    codes,
    billed,
    kind,
    outcome,
    outcomeLabel: OUTCOME_LABEL[outcome],
    recovered,
    resolvedIso: iso,
    resolvedLabel: label,
  };
});

// ---- Appeals sent › Closed ----

export type SentHistoryStatus = 'paid' | 'partly_paid' | 'denied_again' | 'withdrawn';

export const SENT_HISTORY_STATUSES: { value: SentHistoryStatus; label: string; cls: string }[] = [
  { value: 'paid', label: 'Paid', cls: 'b-ok' },
  { value: 'partly_paid', label: 'Partly paid', cls: 'b-warn' },
  { value: 'denied_again', label: 'Denied again', cls: 'b-bad' },
  { value: 'withdrawn', label: 'Withdrawn', cls: 'b-mute' },
];

const SENT_STATUS_LABEL: Record<SentHistoryStatus, string> = {
  paid: 'Paid',
  partly_paid: 'Partly paid',
  denied_again: 'Denied again',
  withdrawn: 'Withdrawn',
};

const SENT_HOW = [
  'Certified mail, return receipt',
  'Certified mail',
  'Fax',
  'the payer’s website',
  'the payer’s secure email',
] as const;

export interface AppealSentHistoryRow {
  id: string;
  patient: string;
  payerShort: string;
  codes: string;
  billed: number;
  sentIso: string;
  sentLabel: string;
  sentHow: string;
  status: SentHistoryStatus;
  statusLabel: string;
  statusCls: string;
  /** What came in on a paid / partly-paid appeal. */
  recovered: number;
}

function pickSentStatus(r: () => number): SentHistoryStatus {
  const n = r();
  if (n < 0.52) return 'paid';
  if (n < 0.74) return 'partly_paid';
  if (n < 0.9) return 'denied_again';
  return 'withdrawn';
}

// ---- Closed (unified) ----
// The "Closed" pill (rcm-18) merges Denied/Underpaid's resolved history with
// Appeals sent's closed history into one card-row shape, so both render with
// the same apl-qrow pattern instead of two different tables. Thin adapters —
// no new mock data; the underlying rows stay as-is above.

export type ClosedSource = 'resolved' | 'sent';

export interface ClosedRow {
  id: string;
  patient: string;
  payer: string;
  codes: string;
  billed: number;
  source: ClosedSource;
  /** Set only for a resolved (Denied/Underpaid) row. */
  kind?: 'Denied' | 'Underpaid';
  statusLabel: string;
  statusCls: string;
  /** Namespaced so the two status vocabularies never collide in one filter. */
  statusValue: string;
  recovered: number;
  dateIso: string;
  dateLabel: string;
  /** The card row's bottom meta line — kind + resolved date, or sent date + channel. */
  metaLine: string;
}

export const CLOSED_STATUS_OPTIONS: { value: string; label: string; cls: string }[] = [
  ...RESOLVED_OUTCOMES.map((o) => ({ value: `resolved:${o.value}`, label: o.label, cls: o.cls })),
  ...SENT_HISTORY_STATUSES.map((s) => ({ value: `sent:${s.value}`, label: s.label, cls: s.cls })),
];

const LIVE_SENT_BADGE: Record<string, { label: string; cls: string }> = {
  paid: { label: 'Paid', cls: 'b-ok' },
  partly_paid: { label: 'Partly paid', cls: 'b-warn' },
  denied_again: { label: 'Denied again', cls: 'b-bad' },
};

/**
 * An appeal closed via Decide this session (store-held `AppealSent`, not the
 * static mock). Same `ClosedRow` shape so it sits in the merged Closed list
 * alongside the ~2,800 history rows instead of its own inline table.
 */
export function closedFromLiveSent(r: AppealSent): ClosedRow {
  const badge = LIVE_SENT_BADGE[r.status] ?? { label: 'Closed', cls: 'b-mute' };
  return {
    id: r.id,
    patient: r.patient,
    payer: r.payerShort,
    codes: r.codes,
    billed: r.billed,
    source: 'sent',
    statusLabel: badge.label,
    statusCls: badge.cls,
    statusValue: `sent:${r.status}`,
    recovered: r.recoveredAmount ?? 0,
    dateIso: r.sentDate,
    dateLabel: r.sentDisplay,
    metaLine: `Sent ${r.sentDisplay} · ${r.sentHow}`,
  };
}

function closedFromResolved(r: DeniedResolvedRow): ClosedRow {
  return {
    id: r.id,
    patient: r.patient,
    payer: r.payer,
    codes: r.codes,
    billed: r.billed,
    source: 'resolved',
    kind: r.kind,
    statusLabel: r.outcomeLabel,
    statusCls: RESOLVED_OUTCOMES.find((o) => o.value === r.outcome)?.cls ?? 'b-mute',
    statusValue: `resolved:${r.outcome}`,
    recovered: r.recovered,
    dateIso: r.resolvedIso,
    dateLabel: r.resolvedLabel,
    metaLine: `${r.kind} · resolved ${r.resolvedLabel}`,
  };
}

/** Appeals sent's closed history (the static ~1,400-row mock). */
export function closedFromSentHistory(r: AppealSentHistoryRow): ClosedRow {
  return {
    id: r.id,
    patient: r.patient,
    payer: r.payerShort,
    codes: r.codes,
    billed: r.billed,
    source: 'sent',
    statusLabel: r.statusLabel,
    statusCls: r.statusCls,
    statusValue: `sent:${r.status}`,
    recovered: r.recovered,
    dateIso: r.sentIso,
    dateLabel: r.sentLabel,
    metaLine: `Sent ${r.sentLabel} · ${r.sentHow}`,
  };
}

export const APPEALS_SENT_HISTORY: AppealSentHistoryRow[] = generate(1400, 0x5a71, (r, i) => {
  const payer = pick(r, PAYERS);
  const codes = pickCodes(r);
  const billed = money(r, 140, 1850);
  const status = pickSentStatus(r);
  const { iso, label } = pastDate(r, 24);
  const how = SENT_HOW[int(r, 0, SENT_HOW.length - 1)] ?? SENT_HOW[0];
  let recovered = 0;
  if (status === 'paid') recovered = Math.round(billed * (0.7 + r() * 0.3));
  else if (status === 'partly_paid') recovered = Math.round(billed * (0.35 + r() * 0.3));
  const statusMeta = SENT_HISTORY_STATUSES.find((s) => s.value === status);
  return {
    id: `CLM-${String(40000 + i)}`,
    patient: fullName(r),
    payerShort: payer,
    codes,
    billed,
    sentIso: iso,
    sentLabel: label,
    sentHow: how,
    status,
    statusLabel: SENT_STATUS_LABEL[status],
    statusCls: statusMeta?.cls ?? 'b-mute',
    recovered,
  };
});

/** The merged Closed pill's data: both histories, one row shape, most recent first. */
export const CLOSED_HISTORY: ClosedRow[] = [
  ...DENIED_RESOLVED.map(closedFromResolved),
  ...APPEALS_SENT_HISTORY.map(closedFromSentHistory),
].sort((a, b) => b.dateIso.localeCompare(a.dateIso));
